import { assertNever } from '@kofno/piper';
import { just, Maybe, nothing } from 'maybeasy';
import { action, computed, makeObservable, observable } from 'mobx';
import { Task } from 'taskarian';
import { callApi } from '../Appy';
import { currentUserStore } from '../CurrentUserStore';
import { groupResourceDecoder } from '../GroupsStore/Decoders';
import { GroupResource } from '../GroupsStore/Types';
import { findLinkT } from '../LinkyLinky';
import { Link } from '../Resource/Types';
import { License } from '../components/People/Participants/AddParticipant/Stores/Types';
import { newGroupResourceDecoder } from './Decoders';
import {
  AvailableCollection,
  loadError,
  loading,
  NewGroupError,
  NewGroupResource,
  ready,
  State,
  submitError,
  submitting,
  success,
  waiting,
} from './Types';

/**
 * Store for the Add Group full-page workflow.
 *
 * Lifecycle:
 *   waiting → loading → ready ⇄ submitting → success | submit-error
 *                     → load-error
 *
 * Also holds the observable form state (name, description, active,
 * selected license/collection IDs, license-picker UI). Kept separate
 * from the state machine so that per-keystroke edits don't churn
 * lifecycle transitions.
 */
class NewGroupStore {
  @observable _state: State = waiting();

  // Form field state — mutated on every keystroke / toggle.
  @observable name = '';
  @observable description = '';
  @observable active = true;
  @observable selectedLicenseIds: number[] = [];
  @observable licenseSearchQuery = '';
  @observable expandedLicenseTypes: Set<string> = new Set();
  @observable selectedCollectionIds: number[] = [];

  constructor() {
    makeObservable(this);
  }

  @computed get state(): State {
    return this._state;
  }

  @computed get canSubmit(): boolean {
    return this._state.kind === 'ready' && this.name.trim().length > 0;
  }

  /**
   * The loaded resource whenever the form is interactable (ready or
   * mid/post-submit). Nothing before load completes and after success,
   * when the form data is not on screen.
   */
  @computed private get formResource(): Maybe<NewGroupResource> {
    switch (this._state.kind) {
      case 'ready':
      case 'submitting':
      case 'submit-error':
        return just(this._state.resource);
      case 'waiting':
      case 'loading':
      case 'load-error':
      case 'success':
        return nothing();
      default:
        return assertNever(this._state);
    }
  }

  @computed get licenses(): ReadonlyArray<License> {
    return this.formResource.map((r) => r.payload.productLicenses).getOrElseValue([]);
  }

  @computed get collections(): ReadonlyArray<AvailableCollection> {
    return this.formResource.map((r) => r.payload.learningCollections).getOrElseValue([]);
  }

  @computed get isSubmitting(): boolean {
    return this._state.kind === 'submitting';
  }

  @action setName = (v: string) => {
    this.name = v;
  };

  @action setDescription = (v: string) => {
    this.description = v;
  };

  @action setActive = (v: boolean) => {
    this.active = v;
  };

  @action setSelectedLicenseIds = (ids: number[]) => {
    this.selectedLicenseIds = ids;
  };

  @action setLicenseSearchQuery = (v: string) => {
    this.licenseSearchQuery = v;
  };

  @action toggleLicenseTypeExpanded = (typeName: string) => {
    const next = new Set(this.expandedLicenseTypes);
    if (next.has(typeName)) {
      next.delete(typeName);
    } else {
      next.add(typeName);
    }
    this.expandedLicenseTypes = next;
  };

  @action setSelectedCollectionIds = (ids: number[]) => {
    this.selectedCollectionIds = ids;
  };

  @action private setState = (next: State) => {
    this._state = next;
  };

  @action load = () => {
    switch (this._state.kind) {
      case 'waiting':
      case 'load-error':
      case 'success':
        this.setState(loading());
        this.runLoad();
        break;
      case 'loading':
      case 'ready':
      case 'submitting':
      case 'submit-error':
        break;
      default:
        assertNever(this._state);
    }
  };

  /**
   * Reset form state so the user can create another group after a
   * successful create. Reuses the resource cached on the `success`
   * state — the license and collection lists don't change between
   * creates in the same session, so returning straight to `ready`
   * avoids the loading-wheel flicker (matching Add Participant).
   * Falls back to a full reload from any other state (defensive; not
   * expected in practice — reset is only called from GroupAdded).
   */
  @action reset = () => {
    this.name = '';
    this.description = '';
    this.active = true;
    this.selectedLicenseIds = [];
    this.licenseSearchQuery = '';
    this.expandedLicenseTypes = new Set();
    this.selectedCollectionIds = [];
    if (this._state.kind === 'success') {
      this.setState(ready(this._state.resource));
    } else {
      this.setState(loading());
      this.runLoad();
    }
  };

  /**
   * Reset to a clean, unloaded state when the Add Group page unmounts, so the
   * next visit re-fetches license/collection availability (seat counts change
   * between visits) and never restores a half-typed draft. The next mount's
   * load() then runs waiting -> loading -> ready.
   */
  @action clear = () => {
    this.name = '';
    this.description = '';
    this.active = true;
    this.selectedLicenseIds = [];
    this.licenseSearchQuery = '';
    this.expandedLicenseTypes = new Set();
    this.selectedCollectionIds = [];
    this.setState(waiting());
  };

  private runLoad = () => {
    Task.succeed<NewGroupError, ReadonlyArray<Link>>(currentUserStore.links)
      .andThen(findLinkT('user-groups-new'))
      .andThen(callApi(newGroupResourceDecoder, {}))
      .fork(
        (error) => this.setState(loadError(error)),
        (resource) => this.setState(ready(resource)),
      );
  };

  @action submit = () => {
    if (this._state.kind !== 'ready') return;
    const resource = this._state.resource;
    this.setState(submitting(resource));

    const status = this.active ? ['active'] : ['inactive'];
    const body = {
      user_group: {
        name: this.name.trim(),
        description: this.description,
        status,
      },
      add_collections: this.selectedCollectionIds,
      add_licenses: this.selectedLicenseIds,
    };

    Task.succeed<NewGroupError, ReadonlyArray<Link>>(resource.links)
      .andThen(findLinkT('create'))
      .andThen(callApi(groupResourceDecoder, body))
      .fork(
        (error) => this.setState(submitError(resource, error)),
        (createdGroup: GroupResource) => this.setState(success(resource, createdGroup)),
      );
  };
}

export const newGroupStore = new NewGroupStore();
export default NewGroupStore;
